import {
  Box,
  Checkbox,
  MenuItem,
  Radio,
  Select,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { SurveySettingsEnums } from "@pulse/pulse-rpcs/lib/types/surveySettings";
import { GridAnswerV1DTO } from "@pulse/pulse-rpcs/lib/types/gridAnswerV1";
import { GridAnswerElementV1Enums } from "@pulse/pulse-rpcs/lib/types/gridAnswerElementV1";
import {
  EMPTY_JSON,
  GridColumnQuestionTypeV1Enum,
  GridColumnQuestionV1Model,
  GridQuestionV1Model,
  GridSingleChoiceDisplayTypeV1Enum,
  MAX_OPEN_ENDED_RESPONSE_LIMIT,
  RICH_TEXT_DEFAULT_FONT_COLOR,
  RICH_TEXT_MARKDOWN_PROPS,
  SmallScreenGridSingleSelectInputField,
  createGridQuestionV1Model,
  isBrowserLightMode,
  isUnansweredGridOpenEndedPlaceholder,
  isUnansweredGridSingleChoicePlaceholder,
  richTextTypography,
  useBorder,
} from "@pulse/shared-components";
import {
  Button,
  useCornerRadius,
  useFoundationColorTokens,
  useShadow,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { RichTextEditor } from "@surya-digital/leo-reactjs-remirror";
import { observer } from "mobx-react";
import { Instance } from "mobx-state-tree";
import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { validateGridAnswer } from "../../../utils/validateGridAnswer";
import { ChatItemContainer } from "./ChatItemContainer";
import { revealNewChatStep } from "./ChatScroll";

export interface ChatGridQuestionLabels {
  cancelEditAnswerText: string;
  nextQuestionText: string;
  sendResponseText: string;
  previousText: string;
  skippedAnswerText: string;
  showMoreText: string;
  showLessText: string;
  editAnswersText: string;
  dropdownPlaceholder: string;
  multiSelectMaxSelectionText: string;
}

interface ChatGridQuestionActiveProps {
  questionDetailsJson: string;
  completedAnswerJson?: string | null;
  isMandatory: boolean;
  initialRowCode?: string;
  labels: ChatGridQuestionLabels;
  fontFamily: string;
  fontScale: SurveySettingsEnums.FontSizeScale.FontSizeScale;
  areActionsDisabled: boolean;
  isEditing: boolean;
  onCancelEdit: () => void;
  onSubmit: (answerJson: string) => void;
  onDraftChange?: (answerJson: string) => void;
}

interface ChatGridQuestionCompletedProps {
  questionDetailsJson: string;
  completedAnswerJson: string;
  labels: Pick<ChatGridQuestionLabels, "skippedAnswerText" | "editAnswersText">;
  areActionsDisabled: boolean;
  showEditActions: boolean;
  onEditRow: (rowCode: string) => void;
}

interface ChatGridQuestionSummaryProps {
  questionDetailsJson: string;
  answerJson?: string;
  skippedAnswerText: string;
  compact?: boolean;
  editAnswerText?: string;
  areActionsDisabled?: boolean;
  onEditRow?: (rowCode: string) => void;
}

type GridModel = Instance<typeof GridQuestionV1Model>;
type GridColumn = Instance<typeof GridColumnQuestionV1Model>;

const createModel = (
  questionDetailsJson: string,
  answerJson?: string | null,
): GridModel => {
  const model = createGridQuestionV1Model();
  model.deserializeQuestionJSON(questionDetailsJson);
  if (answerJson && answerJson !== EMPTY_JSON) {
    const savedAnswer = JSON.parse(answerJson) as GridAnswerV1DTO;
    model.deserialiseAnswerJson(
      JSON.stringify({
        ...savedAnswer,
        answers: savedAnswer.answers.map((cell) => {
          const column = model.gridColumnQuestions.find(
            (item) => item.columnCode === cell.columnCode.code,
          );
          if (cell.answerElement.case === "MULTI_CHOICE") {
            const choice =
              cell.answerElement as GridAnswerElementV1Enums.AnswerElement.MultiChoiceDTO;
            return {
              ...cell,
              answerElement: {
                ...choice,
                multiChoiceAnswerAttributes: {
                  ...choice.multiChoiceAnswerAttributes,
                  selectedChoices:
                    choice.multiChoiceAnswerAttributes.selectedChoices.map(
                      (selected) => ({
                        ...selected,
                        answer:
                          column?.multiSelectQuestion?.options.find(
                            (option) => option.code === selected.code.code,
                          )?.option ?? selected.answer,
                      }),
                    ),
                },
              },
            };
          }
          if (cell.answerElement.case === "SINGLE_CHOICE") {
            const choice =
              cell.answerElement as GridAnswerElementV1Enums.AnswerElement.SingleChoiceDTO;
            const selected = choice.singleChoiceAnswerAttributes;
            return {
              ...cell,
              answerElement: {
                ...choice,
                singleChoiceAnswerAttributes: {
                  ...selected,
                  answer:
                    column?.singleSelectQuestion?.optionTextFromCode(
                      selected.code.code,
                    ) ??
                    (selected.code.code ===
                    column?.singleSelectQuestion?.NAOption.naOptionCode
                      ? column?.singleSelectQuestion?.NAOption.naOptionString
                      : selected.answer),
                },
              },
            };
          }
          return cell;
        }),
      }),
    );
  }
  model.rowOptions.forEach((row) =>
    model.gridColumnQuestions.forEach((column) =>
      model.getGridAnswerModel(
        row.code,
        column.columnCode,
        column.questionType,
      ),
    ),
  );
  return model;
};

const isCellAnswered = (
  model: GridModel,
  rowCode: string,
  column: GridColumn,
): boolean => {
  const answer = model.getGridAnswerModel(
    rowCode,
    column.columnCode,
    column.questionType,
  );
  switch (column.questionType) {
    case GridColumnQuestionTypeV1Enum.OpenEnded:
      return (
        Boolean(answer.openEndedAnswerModel?.answer.trim()) &&
        !isUnansweredGridOpenEndedPlaceholder(
          answer.openEndedAnswerModel?.answer,
        )
      );
    case GridColumnQuestionTypeV1Enum.MultiChoice:
      return answer.isMultiSelectAnswerCompleted;
    case GridColumnQuestionTypeV1Enum.SingleChoice:
      return (
        Boolean(answer.singleChoiceAnswerModel?.code) &&
        !isUnansweredGridSingleChoicePlaceholder(
          answer.singleChoiceAnswerModel?.code,
        )
      );
  }
};

const rowIsValid = (
  model: GridModel,
  rowCode: string,
  isMandatory: boolean,
): boolean => {
  const answered = model.gridColumnQuestions.filter((column) =>
    isCellAnswered(model, rowCode, column),
  ).length;
  if (isMandatory && answered === 0) return false;
  return (
    !model.areAllRowOptionsMandatoryIfRowAttempted ||
    answered === 0 ||
    answered === model.gridColumnQuestions.length
  );
};

const MarkdownText = ({
  text,
  name,
  fontFamily,
  fontScale,
}: {
  text: string;
  name: string;
  fontFamily: string;
  fontScale: SurveySettingsEnums.FontSizeScale.FontSizeScale;
}): React.ReactElement => {
  const isBigScreen = useMediaQuery("(min-width:1200px)");
  return (
    <Box sx={{ minWidth: 0, "& .ProseMirror": { overflowWrap: "anywhere" } }}>
      <RichTextEditor
        name={name}
        initialValue={text}
        typography={richTextTypography(
          fontFamily,
          fontScale,
          RICH_TEXT_DEFAULT_FONT_COLOR,
          isBigScreen,
        )}
        mode="preview"
        supports={RICH_TEXT_MARKDOWN_PROPS}
        borderOnPreview={false}
      />
    </Box>
  );
};

const ChatGridCell = observer(
  ({
    model,
    rowCode,
    column,
    labels,
    fontFamily,
    fontScale,
    onSharedControlChange,
  }: {
    model: GridModel;
    rowCode: string;
    column: GridColumn;
    labels: ChatGridQuestionLabels;
    fontFamily: string;
    fontScale: SurveySettingsEnums.FontSizeScale.FontSizeScale;
    onSharedControlChange: () => void;
  }): React.ReactElement => {
    const tokens = useFoundationColorTokens();
    const spacing = useSpacing();
    const typography = useTypography();
    const radius = useCornerRadius();
    const shadow = useShadow();
    const themeBorder = useBorder();
    const optionBorderColor = `var(--chat-group-option-border-color, ${tokens.border})`;
    const border = {
      ...themeBorder,
      default: `1px solid ${optionBorderColor}`,
    };
    const row = model.rowOptions.find((option) => option.code === rowCode)!;
    const answer = model.getGridAnswerModel(
      rowCode,
      column.columnCode,
      column.questionType,
    );
    const isDarkMode = !isBrowserLightMode();
    const isBigScreen = useMediaQuery("(min-width:1200px)");
    const text = (value: string, name: string): React.ReactElement => (
      <MarkdownText
        text={value}
        name={name}
        fontFamily={fontFamily}
        fontScale={fontScale}
      />
    );
    const choice = (
      code: string,
      label: string,
      selected: boolean,
      multiple: boolean,
      onClick: () => void,
    ): React.ReactElement => (
      <Stack
        key={code}
        component="div"
        role={multiple ? "checkbox" : "radio"}
        aria-checked={selected}
        tabIndex={0}
        direction="row"
        alignItems="center"
        gap={spacing.spaceXS}
        minHeight="44px"
        padding={`${spacing.spaceXS} ${spacing.spaceSM}`}
        border={
          selected
            ? `2px solid ${tokens.borderPrimary}`
            : `1px solid ${optionBorderColor}`
        }
        borderRadius={radius.radiusXS}
        bgcolor={tokens.backgroundElevatedLevel1}
        onClick={(): void => {
          onClick();
          onSharedControlChange();
        }}
        onKeyDown={(event): void => {
          if (event.key === " " || event.key === "Enter") {
            event.preventDefault();
            onClick();
            onSharedControlChange();
          }
        }}
        sx={{
          cursor: "pointer",
          "&:hover": { bgcolor: tokens.backgroundHovered },
        }}
      >
        {multiple ? (
          <Checkbox
            checked={selected}
            disableRipple
            inputProps={{ "aria-hidden": true, tabIndex: -1 }}
            sx={{
              padding: 0,
              pointerEvents: "none",
              color: tokens.iconPrimary,
              "&.Mui-checked": { color: tokens.iconPrimary },
            }}
          />
        ) : (
          <Radio
            checked={selected}
            disableRipple
            inputProps={{ "aria-hidden": true, tabIndex: -1 }}
            sx={{
              padding: 0,
              pointerEvents: "none",
              color: tokens.iconPrimary,
              "&.Mui-checked": { color: tokens.iconPrimary },
            }}
          />
        )}
        {text(label, `chatGridOption-${rowCode}-${column.columnCode}-${code}`)}
      </Stack>
    );

    if (column.questionType === GridColumnQuestionTypeV1Enum.OpenEnded) {
      const field = column.openEndedField;
      if (!field) return <></>;
      return (
        <Stack gap={spacing.spaceXS}>
          {field.fieldTitle &&
            text(
              field.fieldTitle,
              `chatGridField-${rowCode}-${column.columnCode}`,
            )}
          <Box
            component="textarea"
            name={`chatGridText-${rowCode}-${column.columnCode}`}
            value={answer.openEndedAnswerModel?.answer ?? ""}
            placeholder={field.fieldHint}
            aria-label={field.fieldTitle || column.columnHeader || row.option}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>): void => {
              model.setOpenEndedAnswer(
                event.target.value.slice(0, MAX_OPEN_ENDED_RESPONSE_LIMIT),
                field.code,
                rowCode,
                column.columnCode,
              );
              onSharedControlChange();
            }}
            sx={{
              ...typography.b1,
              width: "100%",
              minHeight: "96px",
              boxSizing: "border-box",
              resize: "vertical",
              padding: spacing.spaceSM,
              border: `1px solid ${tokens.border}`,
              borderRadius: radius.radiusXS,
              bgcolor: tokens.backgroundElevatedLevel1,
              color: tokens.label,
            }}
          />
        </Stack>
      );
    }
    if (column.questionType === GridColumnQuestionTypeV1Enum.MultiChoice) {
      const question = column.multiSelectQuestion;
      if (!question) return <></>;
      return (
        <Stack
          gap={spacing.spaceSM}
          role="group"
          aria-label={column.columnHeader}
        >
          {question.maxSelection < question.options.length && (
            <Typography sx={{ ...typography.b2, color: tokens.labelSubtle }}>
              {`${labels.multiSelectMaxSelectionText} ${question.maxSelection}`}
            </Typography>
          )}
          {question.options.map((option) =>
            choice(
              option.code,
              option.option,
              answer.isMultiChoiceOptionSelected({
                code: option.code,
                option: option.option,
              }),
              true,
              () =>
                model.setMultiSelectOptions(
                  { code: option.code, option: option.option },
                  rowCode,
                  column.columnCode,
                ),
            ),
          )}
        </Stack>
      );
    }
    const question = column.singleSelectQuestion;
    if (!question) return <></>;
    if (
      question.singleChoiceDisplayType ===
      GridSingleChoiceDisplayTypeV1Enum.RadioButton
    ) {
      return (
        <Stack
          gap={spacing.spaceSM}
          role="radiogroup"
          aria-label={column.columnHeader}
        >
          {question.options.map((option) =>
            choice(
              option.code,
              option.option,
              answer.isSingleSelectOptionSelected(option.code),
              false,
              () => answer.setSingleSelectAnswer(option.code, option.option),
            ),
          )}
        </Stack>
      );
    }
    if (
      question.singleChoiceDisplayType ===
      GridSingleChoiceDisplayTypeV1Enum.Dropdown
    ) {
      return (
        <Select
          fullWidth
          size="small"
          displayEmpty
          value={answer.singleChoiceAnswerModel?.code ?? ""}
          onChange={(event): void => {
            const code = event.target.value;
            answer.setSingleSelectAnswer(
              code,
              question.optionTextFromCode(code),
            );
            onSharedControlChange();
          }}
          inputProps={{ "aria-label": column.columnHeader || row.option }}
          sx={{ bgcolor: tokens.backgroundElevatedLevel1 }}
        >
          <MenuItem value="" disabled>
            {labels.dropdownPlaceholder}
          </MenuItem>
          {question.options.map((option) => (
            <MenuItem value={option.code} key={option.code}>
              {option.option}
            </MenuItem>
          ))}
        </Select>
      );
    }
    return (
      <SmallScreenGridSingleSelectInputField
        rowOption={row}
        columnQuestion={column}
        gridQuestionV1Model={model}
        border={border}
        tokens={tokens}
        rtTypography={richTextTypography(
          fontFamily,
          fontScale,
          RICH_TEXT_DEFAULT_FONT_COLOR,
          isBigScreen,
        )}
        spacing={spacing}
        radius={radius}
        shadow={shadow}
        isDarkMode={isDarkMode}
        typography={typography}
        fontFamily={fontFamily}
        fontScale={fontScale}
        onAnswerChange={onSharedControlChange}
        miscellaneousSettingsText={undefined}
        isRatingScaleOptionVisible
      />
    );
  },
);

const GridAnswerContent = observer(
  ({
    model,
    rowCode,
    column,
    skippedAnswerText,
    compact = false,
  }: {
    model: GridModel;
    rowCode: string;
    column: GridColumn;
    skippedAnswerText: string;
    compact?: boolean;
  }): React.ReactElement => {
    const tokens = useFoundationColorTokens();
    const typography = useTypography();
    const textStyle = compact
      ? {
          ...typography.b2,
          color: tokens.labelSubtle,
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
        }
      : typography.b1;
    const answer = model.getGridAnswerModel(
      rowCode,
      column.columnCode,
      column.questionType,
    );
    const values =
      column.questionType === GridColumnQuestionTypeV1Enum.OpenEnded
        ? [answer.openEndedAnswerModel?.answer ?? ""]
        : column.questionType === GridColumnQuestionTypeV1Enum.MultiChoice
          ? answer.multiChoiceAnswerModel.map(
              (choice) =>
                column.multiSelectQuestion?.options.find(
                  (option) => option.code === choice.code,
                )?.option ??
                choice.answer ??
                "",
            )
          : [
              column.singleSelectQuestion?.optionTextFromCode(
                answer.singleChoiceAnswerModel?.code ?? "",
              ) ??
                (answer.singleChoiceAnswerModel?.code ===
                column.singleSelectQuestion?.NAOption.naOptionCode
                  ? column.singleSelectQuestion?.NAOption.naOptionString
                  : undefined) ??
                answer.singleChoiceAnswerModel?.answer ??
                column.singleSelectQuestion?.NAOption.naOptionString ??
                "",
            ];
    const present = values.filter((value) => value.trim() !== "");
    if (!isCellAnswered(model, rowCode, column) || present.length === 0) {
      return <Typography sx={textStyle}>{skippedAnswerText}</Typography>;
    }
    return (
      <Stack gap={compact ? 0 : "2px"}>
        {present.map((value, index) => (
          <Typography key={`${index}:${value}`} sx={textStyle}>
            {`• ${value}`}
          </Typography>
        ))}
      </Stack>
    );
  },
);

const COLLAPSED_ROW_SUMMARY_HEIGHT = 6 * 24;

const GridPreviousRowSummary = observer(
  ({
    model,
    rowIndex,
    labels,
  }: {
    model: GridModel;
    rowIndex: number;
    labels: ChatGridQuestionLabels;
  }): React.ReactElement => {
    const tokens = useFoundationColorTokens();
    const spacing = useSpacing();
    const typography = useTypography();
    const row = model.rowOptions[rowIndex];
    const contentRef = useRef<HTMLDivElement>(null);
    const contentInnerRef = useRef<HTMLDivElement>(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const checkOverflow = useCallback((): void => {
      const content = contentRef.current;
      if (content) {
        setIsOverflowing(
          content.scrollHeight > COLLAPSED_ROW_SUMMARY_HEIGHT + 1,
        );
      }
    }, []);

    useLayoutEffect(() => {
      checkOverflow();
      const content = contentInnerRef.current;
      if (!content) return undefined;
      const resizeObserver = new ResizeObserver(checkOverflow);
      resizeObserver.observe(content);
      return (): void => resizeObserver.disconnect();
    }, [checkOverflow]);

    return (
      <Stack gap={spacing.spaceXXS}>
        <Typography
          sx={{ ...typography.b2, fontWeight: 600, color: tokens.labelSubtle }}
        >
          {`${rowIndex + 1}/${model.rowOptions.length} ${row.option}`}
        </Typography>
        <Stack
          gap={spacing.spaceXXS}
          alignItems="flex-start"
          padding={spacing.spaceSM}
          sx={{ bgcolor: tokens.backgroundSubtle, borderRadius: "14px" }}
        >
          <Box
            ref={contentRef}
            width="100%"
            sx={{
              maxHeight: isExpanded
                ? undefined
                : `${COLLAPSED_ROW_SUMMARY_HEIGHT}px`,
              overflow: "hidden",
            }}
          >
            <Stack ref={contentInnerRef}>
              {model.gridColumnQuestions.map((column) => (
                <Stack key={column.columnCode}>
                  {column.columnHeader && (
                    <Typography
                      sx={{
                        ...typography.b2,
                        color: tokens.labelSubtle,
                      }}
                    >
                      {column.columnHeader}
                    </Typography>
                  )}
                  <GridAnswerContent
                    model={model}
                    rowCode={row.code}
                    column={column}
                    skippedAnswerText={labels.skippedAnswerText}
                    compact
                  />
                </Stack>
              ))}
            </Stack>
          </Box>
          {isOverflowing && (
            <Box
              component="button"
              type="button"
              onClick={(): void => setIsExpanded((expanded) => !expanded)}
              sx={{
                border: 0,
                padding: 0,
                background: "transparent",
                color: tokens.labelSubtle,
                font: "inherit",
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              {isExpanded ? labels.showLessText : labels.showMoreText}
            </Box>
          )}
        </Stack>
      </Stack>
    );
  },
);

const EditButton = ({
  label,
  disabled,
  onClick,
  inheritColor = false,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  inheritColor?: boolean;
}): React.ReactElement => {
  const tokens = useFoundationColorTokens();
  const typography = useTypography();
  const spacing = useSpacing();
  return (
    <Box
      component="button"
      type="button"
      name="editChatGridAnswers"
      disabled={disabled}
      onClick={onClick}
      sx={{
        ...typography.b2,
        alignSelf: "flex-end",
        border: 0,
        background: "transparent",
        color: inheritColor ? "inherit" : tokens.label,
        cursor: disabled ? "default" : "pointer",
        fontWeight: 600,
        padding: `${spacing.spaceXS} ${spacing.spaceSM}`,
        "&:hover": { textDecoration: "underline" },
      }}
    >
      {label}
    </Box>
  );
};

export const ChatGridQuestionSummary = ({
  questionDetailsJson,
  answerJson,
  skippedAnswerText,
  compact = false,
  editAnswerText,
  areActionsDisabled = false,
  onEditRow,
}: ChatGridQuestionSummaryProps): React.ReactElement => {
  const spacing = useSpacing();
  const model = useMemo(
    () => createModel(questionDetailsJson, answerJson),
    [questionDetailsJson, answerJson],
  );
  const typography = useTypography();
  if (!answerJson || answerJson === EMPTY_JSON) {
    return (
      <Stack gap={spacing.spaceXXS}>
        <Typography sx={compact ? typography.b2 : typography.b1}>
          {skippedAnswerText}
        </Typography>
        {onEditRow && model.rowOptions[0] && editAnswerText && (
          <EditButton
            label={editAnswerText}
            disabled={areActionsDisabled}
            onClick={(): void => onEditRow(model.rowOptions[0].code)}
            inheritColor
          />
        )}
      </Stack>
    );
  }
  return (
    <Stack gap={compact ? "8px" : "12px"}>
      {model.rowOptions.map((row, rowIndex) => (
        <Stack key={row.code} gap={spacing.spaceXXS}>
          <Typography sx={compact ? typography.b2 : typography.b1}>
            {`${rowIndex + 1}/${model.rowOptions.length} ${row.option}`}
          </Typography>
          {model.gridColumnQuestions.map((column) => (
            <Stack key={column.columnCode} gap="2px">
              {column.columnHeader && model.gridColumnQuestions.length > 1 && (
                <Typography sx={compact ? typography.b2 : typography.b1}>
                  {column.columnHeader}
                </Typography>
              )}
              <GridAnswerContent
                model={model}
                rowCode={row.code}
                column={column}
                skippedAnswerText={skippedAnswerText}
                compact={compact}
              />
            </Stack>
          ))}
          {onEditRow && editAnswerText && (
            <EditButton
              label={editAnswerText}
              disabled={areActionsDisabled}
              onClick={(): void => onEditRow(row.code)}
              inheritColor
            />
          )}
        </Stack>
      ))}
    </Stack>
  );
};

export const ChatGridQuestionActive = observer(
  ({
    questionDetailsJson,
    completedAnswerJson,
    isMandatory,
    initialRowCode,
    labels,
    fontFamily,
    fontScale,
    areActionsDisabled,
    isEditing,
    onCancelEdit,
    onSubmit,
    onDraftChange,
  }: ChatGridQuestionActiveProps): React.ReactElement => {
    const spacing = useSpacing();
    const [model] = useState(() =>
      createModel(questionDetailsJson, completedAnswerJson),
    );
    const [rowIndex, setRowIndex] = useState(() =>
      Math.max(
        0,
        model.rowOptions.findIndex((row) => row.code === initialRowCode),
      ),
    );
    const [columnIndex, setColumnIndex] = useState(0);
    const activeStepRef = useRef<HTMLDivElement>(null);
    const previousStep = useRef({ rowIndex, columnIndex });
    useLayoutEffect(() => {
      const previous = previousStep.current;
      const isMovingForward =
        rowIndex > previous.rowIndex ||
        (rowIndex === previous.rowIndex && columnIndex > previous.columnIndex);
      previousStep.current = { rowIndex, columnIndex };
      if (!isMovingForward || isEditing) return undefined;

      const frame = requestAnimationFrame(() => {
        revealNewChatStep(activeStepRef.current);
      });
      return (): void => cancelAnimationFrame(frame);
    }, [rowIndex, columnIndex, isEditing]);
    const [, setControlRevision] = useState(0);
    const tokens = useFoundationColorTokens();
    const typography = useTypography();
    const row = model.rowOptions[rowIndex];
    const columns = model.gridColumnQuestions;
    const multipleColumns = columns.length > 1;
    const currentColumn = columns[columnIndex];
    if (!row || !currentColumn) return <></>;

    const rowValid = rowIsValid(model, row.code, isMandatory);
    const currentRequired =
      isMandatory && model.areAllRowOptionsMandatoryIfRowAttempted;
    const lastColumn = columnIndex === columns.length - 1;
    const lastRow = rowIndex === model.rowOptions.length - 1;
    const endOfRow = !multipleColumns || lastColumn;
    const submitting = endOfRow && (isEditing || lastRow);
    const allRowsValid = model.rowOptions.every((option) =>
      rowIsValid(model, option.code, isMandatory),
    );
    const canAdvance =
      !areActionsDisabled &&
      (!currentRequired || isCellAnswered(model, row.code, currentColumn)) &&
      (!endOfRow || rowValid) &&
      (!isEditing || !submitting || allRowsValid) &&
      (!isEditing ||
        !submitting ||
        validateGridAnswer(isMandatory, model) === null);
    const submit = (): void => {
      if (!allRowsValid || validateGridAnswer(isMandatory, model) !== null) {
        return;
      }
      const answerJson = model.serializedAnswerJSON;
      const hasAnswers = JSON.parse(answerJson).answers.length > 0;
      onSubmit(hasAnswers ? answerJson : EMPTY_JSON);
    };
    const next = (): void => {
      if (!canAdvance) return;
      if (multipleColumns) {
        if (!lastColumn) {
          setColumnIndex(columnIndex + 1);
        } else if (isEditing || lastRow) {
          submit();
        } else {
          setRowIndex(rowIndex + 1);
          setColumnIndex(0);
        }
      } else if (lastRow) {
        submit();
      } else {
        setRowIndex(rowIndex + 1);
      }
    };
    const previous = (): void => {
      if (multipleColumns && columnIndex > 0) {
        setColumnIndex(columnIndex - 1);
      } else if (rowIndex > 0 && !isEditing) {
        setRowIndex(rowIndex - 1);
        setColumnIndex(multipleColumns ? columns.length - 1 : 0);
      } else if (!multipleColumns && rowIndex > 0) {
        setRowIndex(rowIndex - 1);
      }
    };
    const onSharedControlChange = (): void => {
      queueMicrotask(() => {
        // Slider controls commit their selected code during serialization.
        const answerJson = model.serializedAnswerJSON;
        if (answerJson) {
          setControlRevision((current) => current + 1);
          onDraftChange?.(answerJson);
        }
      });
    };

    return (
      <Stack gap={spacing.spaceSM}>
        {multipleColumns &&
          !isEditing &&
          model.rowOptions
            .slice(0, rowIndex)
            .map((previousRow, index) => (
              <GridPreviousRowSummary
                key={previousRow.code}
                model={model}
                rowIndex={index}
                labels={labels}
              />
            ))}
        {!multipleColumns &&
          model.rowOptions.slice(0, rowIndex).map((previousRow, index) => (
            <Stack key={previousRow.code} gap={spacing.spaceXXS}>
              <Typography
                sx={{
                  ...typography.b2,
                  fontWeight: 600,
                  color: tokens.labelSubtle,
                }}
              >
                {`${index + 1}/${model.rowOptions.length} ${previousRow.option}`}
              </Typography>
              <Box
                padding={spacing.spaceSM}
                sx={{ bgcolor: tokens.backgroundSubtle, borderRadius: "14px" }}
              >
                <GridAnswerContent
                  model={model}
                  rowCode={previousRow.code}
                  column={columns[0]}
                  skippedAnswerText={labels.skippedAnswerText}
                  compact
                />
              </Box>
            </Stack>
          ))}
        <Box ref={activeStepRef}>
          <Typography
            sx={{ ...typography.b2, fontWeight: 600, color: tokens.label }}
          >
            {`${rowIndex + 1}/${model.rowOptions.length} ${row.option}`}
          </Typography>
        </Box>
        {multipleColumns ? (
          <Box sx={{ overflow: "hidden", minWidth: 0 }}>
            <Stack
              direction="row"
              alignItems="stretch"
              gap={spacing.spaceMD}
              sx={{ "--chat-grid-card-width": "min(400px, 100%)" }}
            >
              {columns.map((column, index) => {
                const isActiveColumn = index === columnIndex;
                return (
                  <Box
                    key={column.columnCode}
                    ref={(node: HTMLDivElement | null): void => {
                      node?.toggleAttribute("inert", !isActiveColumn);
                    }}
                    aria-hidden={!isActiveColumn}
                    sx={{
                      flex: "0 0 var(--chat-grid-card-width)",
                      minWidth: 0,
                      boxSizing: "border-box",
                      padding: "12px",
                      bgcolor: tokens.backgroundSubtle,
                      borderRadius: "14px",
                      opacity: isActiveColumn ? 1 : 0.5,
                      "--chat-group-option-border-color": isBrowserLightMode()
                        ? tokens.border
                        : tokens.borderHighEmphasis,
                      transform: `translateX(calc(-${columnIndex * 100}% - ${columnIndex * 16}px))`,
                      transition: "transform 250ms ease",
                      "@media (prefers-reduced-motion: reduce)": {
                        transition: "none",
                      },
                    }}
                  >
                    <Stack gap={spacing.spaceXS}>
                      <Stack
                        direction="row"
                        gap={spacing.spaceXXS}
                        aria-label={`${index + 1} of ${columns.length}`}
                      >
                        {columns.map((item, dotIndex) => (
                          <Box
                            key={item.columnCode}
                            aria-hidden="true"
                            sx={{
                              width: dotIndex === index ? "20px" : "8px",
                              height: "8px",
                              borderRadius: "10px",
                              bgcolor:
                                dotIndex === index && isActiveColumn
                                  ? tokens.backgroundPrimary
                                  : tokens.borderHighEmphasis,
                              opacity: dotIndex === index ? 1 : 0.55,
                            }}
                          />
                        ))}
                      </Stack>
                      {column.columnHeader && (
                        <Typography
                          sx={{
                            ...typography.b2,
                            fontWeight: 600,
                            color: tokens.label,
                          }}
                        >
                          {column.columnHeader}
                        </Typography>
                      )}
                      <ChatGridCell
                        model={model}
                        rowCode={row.code}
                        column={column}
                        labels={labels}
                        fontFamily={fontFamily}
                        fontScale={fontScale}
                        onSharedControlChange={onSharedControlChange}
                      />
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        ) : (
          <Box
            padding={spacing.spaceSM}
            sx={{
              bgcolor: tokens.backgroundSubtle,
              borderRadius: "14px",
              "--chat-group-option-border-color": isBrowserLightMode()
                ? tokens.border
                : tokens.borderHighEmphasis,
            }}
          >
            <ChatGridCell
              model={model}
              rowCode={row.code}
              column={currentColumn}
              labels={labels}
              fontFamily={fontFamily}
              fontScale={fontScale}
              onSharedControlChange={onSharedControlChange}
            />
          </Box>
        )}
        <Stack
          data-chat-item-actions
          direction="row"
          justifyContent="flex-end"
          gap={spacing.spaceXS}
          flexWrap="wrap"
          sx={{
            position: "sticky",
            bottom: 0,
            zIndex: 1,
            backgroundColor: tokens.backgroundElevatedLevel1,
            paddingBlock: "12px",
          }}
        >
          {isEditing && (
            <Button
              name="cancelEditChatSurveyAnswer"
              variant="outlined-neutral"
              label={labels.cancelEditAnswerText}
              size="medium"
              disabled={areActionsDisabled}
              onClick={onCancelEdit}
            />
          )}
          {(multipleColumns || rowIndex > 0) && (
            <Button
              name="previousGridQuestion"
              variant="outlined-neutral"
              label={labels.previousText}
              size="medium"
              disabled={
                areActionsDisabled ||
                (columnIndex === 0 && (rowIndex === 0 || isEditing))
              }
              onClick={previous}
            />
          )}
          <Button
            name="nextGridQuestion"
            variant="filled"
            size="medium"
            disabled={!canAdvance}
            onClick={next}
            label={
              submitting ? labels.sendResponseText : labels.nextQuestionText
            }
          />
        </Stack>
      </Stack>
    );
  },
);

export const ChatGridQuestionCompleted = ({
  questionDetailsJson,
  completedAnswerJson,
  labels,
  areActionsDisabled,
  showEditActions,
  onEditRow,
}: ChatGridQuestionCompletedProps): React.ReactElement => {
  const spacing = useSpacing();
  const model = useMemo(
    () => createModel(questionDetailsJson, completedAnswerJson),
    [questionDetailsJson, completedAnswerJson],
  );
  const typography = useTypography();
  const { t } = useTranslation();
  const multipleColumns = model.gridColumnQuestions.length > 1;
  if (completedAnswerJson === EMPTY_JSON) {
    return (
      <Stack gap={spacing.spaceXS} alignItems="flex-end">
        <ChatItemContainer
          alignment="outgoing"
          ariaLabel={t("surveys.chatInterface.surveyAnswerAriaLabel")}
        >
          <Typography sx={typography.b1}>{labels.skippedAnswerText}</Typography>
        </ChatItemContainer>
        {showEditActions && (
          <EditButton
            label={labels.editAnswersText}
            disabled={areActionsDisabled}
            onClick={(): void => onEditRow(model.rowOptions[0].code)}
          />
        )}
      </Stack>
    );
  }
  if (!multipleColumns) {
    return (
      <Stack gap={spacing.spaceXS} alignItems="flex-end">
        <ChatItemContainer
          alignment="outgoing"
          ariaLabel={t("surveys.chatInterface.surveyAnswerAriaLabel")}
        >
          <Stack gap={spacing.spaceSM}>
            {model.rowOptions.map((row, index) => (
              <Stack key={row.code} gap={spacing.spaceXXS}>
                {index > 0 && (
                  <Box
                    sx={{ borderTop: "1px solid currentColor", opacity: 0.35 }}
                  />
                )}
                <Typography sx={typography.b1}>
                  {`${index + 1}/${model.rowOptions.length} ${row.option}`}
                </Typography>
                <GridAnswerContent
                  model={model}
                  rowCode={row.code}
                  column={model.gridColumnQuestions[0]}
                  skippedAnswerText={labels.skippedAnswerText}
                />
              </Stack>
            ))}
          </Stack>
        </ChatItemContainer>
        {showEditActions && (
          <EditButton
            label={labels.editAnswersText}
            disabled={areActionsDisabled}
            onClick={(): void => onEditRow(model.rowOptions[0].code)}
          />
        )}
      </Stack>
    );
  }
  return (
    <Stack gap={spacing.spaceMD}>
      {model.rowOptions.map((row, rowIndex) => (
        <Stack key={row.code} gap={spacing.spaceXS} alignItems="flex-end">
          <ChatItemContainer
            alignment="outgoing"
            ariaLabel={t("surveys.chatInterface.surveyAnswerAriaLabel")}
          >
            <Stack gap="10px">
              <Typography sx={typography.b1}>
                {`${rowIndex + 1}/${model.rowOptions.length} ${row.option}`}
              </Typography>
              <Box
                sx={{ borderTop: "1px solid currentColor", opacity: 0.35 }}
              />
              {model.gridColumnQuestions.map((column, index) => (
                <Stack key={column.columnCode} gap="2px">
                  {index > 0 && (
                    <Box
                      sx={{
                        borderTop: "1px solid currentColor",
                        opacity: 0.35,
                        marginBottom: "8px",
                      }}
                    />
                  )}
                  {column.columnHeader && (
                    <Typography sx={typography.b1}>
                      {column.columnHeader}
                    </Typography>
                  )}
                  <GridAnswerContent
                    model={model}
                    rowCode={row.code}
                    column={column}
                    skippedAnswerText={labels.skippedAnswerText}
                  />
                </Stack>
              ))}
            </Stack>
          </ChatItemContainer>
          {showEditActions && (
            <EditButton
              label={labels.editAnswersText}
              disabled={areActionsDisabled}
              onClick={(): void => onEditRow(row.code)}
            />
          )}
        </Stack>
      ))}
    </Stack>
  );
};
